import { useMemo } from 'react'
import type { FlightsClassTypes } from 'constants/flight'
import { CHARTER_CASH_CABINS } from 'lib/maldivesCharter/charterCabins'
import { REWARD_PREVIEW_RATE_BY_CABIN } from 'lib/maldivesCharter/classicRewardPricing'
import { aggregateMaldivesCharterMonthTileRewardPricing } from '../MaldivesDirectCalendar/maldivesCharterMonthTileRewardPricing'
import type { CalendarItemBundleData, DateRange } from './types'
import type { MaldivesCabinHotelEntry } from './useMaldivesOffersPricing'
import type useCharterRewardSeatsIndicatorForDate from './useCharterRewardSeatsIndicatorForDate'
import type { CabinDateJourneys } from './charterRewardAvailabilityContext'

interface Params {
  dateRangesByMonth: Record<string, Array<DateRange>>
  journeysByCabinDate: Map<FlightsClassTypes, Map<string, CabinDateJourneys>>
  rewardIndicator: ReturnType<typeof useCharterRewardSeatsIndicatorForDate>
  cabinFilter: FlightsClassTypes | null
  hotelPriceByCabinAndDate?: Partial<Record<FlightsClassTypes, Record<string, MaldivesCabinHotelEntry>>>
  itemBundleDataByDate?: Record<string, CalendarItemBundleData>
  luxPlusEnabled: boolean
  seatEligiblePassengers: number
  rewardSeatsOnly: boolean
  rewardLockedForLogin: boolean
  priceMode?: 'total' | 'perPerson'
  totalPassengers?: number
}

interface MonthRewardState {
  /** Hardcoded preview prices for logged-out/non-member users, per month. */
  rewardPreviewByMonth: Record<string, { points: number; cash: number }>
  /** Per-month seal visibility (at least one date has all reward legs available). */
  monthRewardSeals: Record<string, { showRewardSeal: boolean }>
  /** Cheapest real reward price per month from availability data (reward-seats-only mode). */
  monthRewardPricing: Record<string, { points: number; cash: number }>
}

/**
 * Derives per-month reward display state shared by desktop and mobile Maldives charter calendars:
 * preview pricing for non-members, reward seal visibility, and cheapest real reward pricing.
 */
export function useMaldivesCharterMonthRewardState({
  dateRangesByMonth,
  journeysByCabinDate,
  rewardIndicator,
  cabinFilter,
  hotelPriceByCabinAndDate,
  itemBundleDataByDate,
  luxPlusEnabled,
  seatEligiblePassengers,
  rewardSeatsOnly,
  rewardLockedForLogin,
  priceMode = 'total',
  totalPassengers = 0,
}: Params): MonthRewardState {
  const cabinsToCheck = useMemo(() => cabinFilter ? [cabinFilter] : [...CHARTER_CASH_CABINS], [cabinFilter])

  const rewardPreviewByMonth = useMemo(() => {
    if (!rewardLockedForLogin) return {}
    let cheapestRate: { pointsPerPerson: number; cashPerPerson: number } | undefined
    for (const cabin of cabinsToCheck) {
      const rate = REWARD_PREVIEW_RATE_BY_CABIN[cabin]
      if (rate && (!cheapestRate || rate.cashPerPerson < cheapestRate.cashPerPerson)) {
        cheapestRate = rate
      }
    }
    if (!cheapestRate) return {}
    const flightPoints = cheapestRate.pointsPerPerson * seatEligiblePassengers
    const flightCash = cheapestRate.cashPerPerson * seatEligiblePassengers

    const result: Record<string, { points: number; cash: number }> = {}
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      let cheapestHotel: number | undefined
      dateRanges.forEach(({ checkIn }) => {
        for (const cabin of cabinsToCheck) {
          const h = hotelPriceByCabinAndDate?.[cabin]?.[checkIn]
          const hp = h ? (luxPlusEnabled ? (h.hotelMemberPrice || h.hotelPrice || 0) : (h.hotelPrice || 0)) : 0
          if (hp > 0 && (cheapestHotel === undefined || hp < cheapestHotel)) {
            cheapestHotel = hp
          }
        }
        if (cheapestHotel === undefined) {
          const itemData = itemBundleDataByDate?.[checkIn]
          if (itemData?.available && itemData.hotelPrice && itemData.hotelPrice > 0) {
            if (cheapestHotel === undefined || itemData.hotelPrice < cheapestHotel) {
              cheapestHotel = itemData.hotelPrice
            }
          }
        }
      })
      if (cheapestHotel !== undefined) {
        const rawPoints = flightPoints
        const rawCash = cheapestHotel + flightCash
        const divisor = priceMode === 'perPerson' && totalPassengers > 0 ? totalPassengers : 1
        result[monthName] = { points: rawPoints / divisor, cash: rawCash / divisor }
      }
    })
    return result
  }, [rewardLockedForLogin, cabinsToCheck, seatEligiblePassengers, dateRangesByMonth, hotelPriceByCabinAndDate, itemBundleDataByDate, luxPlusEnabled, priceMode, totalPassengers])

  const monthRewardSeals = useMemo(() => {
    const datesByMonth: Record<string, Array<{ dateKey: string; allLegsAvailable: boolean }>> = {}
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      datesByMonth[monthName] = dateRanges.map(({ checkIn }) => {
        const anyCabinAvailable = cabinsToCheck.some((cabin) => {
          const entry = journeysByCabinDate.get(cabin)?.get(checkIn)
          const depKey = entry?.departing?.journeyKey
          const retKey = entry?.returning?.journeyKey
          if (!depKey || !retKey) return false
          return rewardIndicator.getLegResult(depKey)?.available === true
            && rewardIndicator.getLegResult(retKey)?.available === true
        })
        return { dateKey: checkIn, allLegsAvailable: anyCabinAvailable }
      })
    })
    return aggregateMaldivesCharterMonthTileRewardPricing({ datesByMonth })
  }, [dateRangesByMonth, journeysByCabinDate, rewardIndicator, cabinsToCheck])

  const monthRewardPricing = useMemo(() => {
    if (!rewardSeatsOnly) return {}
    const result: Record<string, { points: number; cash: number }> = {}
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      let lowestCash: number | undefined
      let correspondingPoints: number | undefined
      dateRanges.forEach(({ checkIn }) => {
        const itemData = itemBundleDataByDate?.[checkIn]
        for (const cabin of cabinsToCheck) {
          const entry = journeysByCabinDate.get(cabin)?.get(checkIn)
          if (!entry?.departing || !entry?.returning) continue
          if (!entry.departing.journeyKey || !entry.returning.journeyKey) continue
          const depResult = rewardIndicator.getLegResult(entry.departing.journeyKey)
          const retResult = rewardIndicator.getLegResult(entry.returning.journeyKey)
          if (!depResult?.available || !retResult?.available) continue
          if (depResult.pointsPrice == null || retResult.pointsPrice == null) continue
          if (depResult.cashPrice == null || retResult.cashPrice == null) continue

          const cabinHotel = hotelPriceByCabinAndDate?.[cabin]?.[checkIn]
          let hotelForReward = 0
          if (cabinHotel) {
            hotelForReward = luxPlusEnabled
              ? (cabinHotel.hotelMemberPrice || cabinHotel.hotelPrice || 0)
              : (cabinHotel.hotelPrice || 0)
          } else if (itemData?.available) {
            const cabinFlightFare = (entry.departing.price?.all?.totalFare ?? 0) + (entry.returning.price?.all?.totalFare ?? 0)
            const bundleTotal = luxPlusEnabled ? (itemData.memberTotalPrice || itemData.totalPrice || 0) : (itemData.totalPrice || 0)
            hotelForReward = Math.max(0, bundleTotal - cabinFlightFare)
          }
          if (!hotelForReward) continue

          const rawPoints = (depResult.pointsPrice + retResult.pointsPrice) * seatEligiblePassengers
          const rawCash = (depResult.cashPrice + retResult.cashPrice) * seatEligiblePassengers + hotelForReward
          const divisor = priceMode === 'perPerson' && totalPassengers > 0 ? totalPassengers : 1
          const totalPoints = rawPoints / divisor
          const totalCash = rawCash / divisor

          if (lowestCash === undefined || totalCash < lowestCash) {
            lowestCash = totalCash
            correspondingPoints = totalPoints
          }
        }
      })
      if (correspondingPoints !== undefined && lowestCash !== undefined) {
        result[monthName] = { points: correspondingPoints, cash: lowestCash }
      }
    })
    return result
  }, [rewardSeatsOnly, cabinsToCheck, dateRangesByMonth, itemBundleDataByDate, journeysByCabinDate, seatEligiblePassengers, rewardIndicator, hotelPriceByCabinAndDate, luxPlusEnabled, priceMode, totalPassengers])

  return { rewardPreviewByMonth, monthRewardSeals, monthRewardPricing }
}
